/**
 * HeaderView - Similar players header with filtering accordion
 * @module features/similar-players/components/Header
 */

import { TEST_ID_SIMILAR_PLAYER } from "e2e/testId";
import { SlidersHorizontalIcon } from "lucide-react-native";
import React, { memo } from "react";
import { Dimensions, Platform, type ViewStyle } from "react-native";

import { ScrollView } from "@/components/atoms/ScrollView";
import { NestableScrollContainer } from "react-native-draggable-flatlist";

import FilterFieldsView from "@/features/similar-players/components/organisms/FilterFields";

import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
} from "@/components/atoms/Accordion";
import { Badge, BadgeText } from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";
import {
  RosterManagementLeagueFragment,
  RosterManagementNationalityFragment,
} from "@/generated/graphql";

const MAX_HEIGHT_ACCORDION = Dimensions.get("window").height * 0.5;

/*
 * design-system: dynamic-style waiver — measured accordion max-height (50% of
 * the window) has no closed sizing token; kept as a stable style reference so
 * no inline object literal renders on the JSX element.
 */
const ACCORDION_MAX_HEIGHT_STYLE: ViewStyle = {
  maxHeight: MAX_HEIGHT_ACCORDION,
};

/**
 * Props for HeaderView component
 */
interface Props {
  playerName?: string;
  activeFilterCount: number;
  groupedLeagues: RosterManagementLeagueFragment[];
  loadingLeagues: boolean;
  leaguesValue: RosterManagementLeagueFragment[];
  nationalities: RosterManagementNationalityFragment[];
  loadingNationalities: boolean;
  nationalitiesValue: RosterManagementNationalityFragment[];
  ageValue: number[];
  salaryValue: number[];
  monthsRemainingValue: number[];
  valueAccordion?: string[];
  isSmallScreen: boolean;
  onSelectLeagues?: (value: RosterManagementLeagueFragment[]) => void;
  onSelectNationalities?: (
    value: RosterManagementNationalityFragment[]
  ) => void;
  onChangeAge?: (value: number[]) => void;
  onChangeSalary?: (value: number[]) => void;
  onChangeMonthsRemaining?: (value: number[]) => void;
  onReset?: () => void;
  onPressAccordion?: () => void;
  isDark: boolean;
}

/**
 * HeaderView component displays the similar players header with filtering accordion.
 * @param root0 - Component props
 * @param root0.playerName - Name of the player to find similar players for
 * @param root0.activeFilterCount - Number of active filters applied
 * @param root0.groupedLeagues - Array of available leagues to filter by
 * @param root0.loadingLeagues - Loading state for leagues data
 * @param root0.leaguesValue - Currently selected leagues
 * @param root0.nationalities - Array of available nationalities to filter by
 * @param root0.loadingNationalities - Loading state for nationalities data
 * @param root0.nationalitiesValue - Currently selected nationalities
 * @param root0.ageValue - Current age range filter values [min, max]
 * @param root0.salaryValue - Current salary range filter values [min, max]
 * @param root0.monthsRemainingValue - Current months remaining range filter values [min, max]
 * @param root0.valueAccordion - Current accordion expanded state values
 * @param root0.isSmallScreen - Whether the screen is mobile sized
 * @param root0.onSelectLeagues - Callback when leagues selection changes
 * @param root0.onSelectNationalities - Callback when nationalities selection changes
 * @param root0.onChangeAge - Callback when age range changes
 * @param root0.onChangeSalary - Callback when salary range changes
 * @param root0.onChangeMonthsRemaining - Callback when months remaining range changes
 * @param root0.onReset - Callback to reset all filters
 * @param root0.onPressAccordion - Callback when accordion trigger is pressed
 * @param root0.isDark - Whether dark theme is active
 */
const HeaderView = ({
  playerName,
  activeFilterCount = 0,
  groupedLeagues,
  loadingLeagues,
  leaguesValue,
  nationalities,
  loadingNationalities,
  nationalitiesValue,
  ageValue,
  salaryValue,
  monthsRemainingValue,
  valueAccordion,
  isSmallScreen,
  onSelectLeagues,
  onSelectNationalities,
  onChangeAge,
  onChangeSalary,
  onChangeMonthsRemaining,
  onReset,
  onPressAccordion,
  isDark,
}: Props) => (
  <Accordion
    variant="unfilled"
    type="single"
    isCollapsible
    value={valueAccordion}
    className={!isSmallScreen ? "" : "px-4 pt-8"}
  >
    <AccordionItem
      value="filter"
      className={`rounded-md border ${isDark ? "border-surface-muted" : "border-surface-strong"} bg-transparent`}
    >
      <AccordionHeader className="flex flex-row items-center p-4">
        <Box className="flex-1">
          {!!playerName && (
            <Text
              variant="unstyled"
              className={`shrink pr-4 ${!isSmallScreen ? "text-title" : "text-title-lg"}`}
            >
              Similar to{" "}
              <Text variant={!isSmallScreen ? "title" : "title-lg"}>
                {playerName}
              </Text>
            </Text>
          )}
        </Box>
        <AccordionTrigger
          testID={TEST_ID_SIMILAR_PLAYER.ACCORDION_TRIGGER}
          className="w-auto p-0"
          hitSlop={16}
          onPress={onPressAccordion}
        >
          {() => (
            <Stack>
              {activeFilterCount > 0 && (
                <Badge
                  className="absolute -right-3 -top-5 z-10 size-5 items-center justify-center self-end rounded-full bg-status-info"
                  variant="solid"
                >
                  <BadgeText className="text-content-on-accent">
                    {activeFilterCount}
                  </BadgeText>
                </Badge>
              )}
              <Icon as={SlidersHorizontalIcon} className="font-body" />
            </Stack>
          )}
        </AccordionTrigger>
      </AccordionHeader>
      <AccordionContent className="gap-4 p-0 pb-4">
        {Platform.OS === "web" ? (
          <ScrollView
            // eslint-disable-next-line no-restricted-syntax -- ScrollView atom re-exports RN ScrollView (no UNSAFE_style forwarding); runtime 50%-viewport accordion maxHeight has no closed sizing token
            style={ACCORDION_MAX_HEIGHT_STYLE}
            className="flex-1"
            contentContainerClassName={
              !isSmallScreen ? "gap-4 px-6" : "gap-10 px-6 pt-10"
            }
            showsVerticalScrollIndicator={false}
          >
            <FilterFieldsView
              groupedLeagues={groupedLeagues}
              loadingLeagues={loadingLeagues}
              leaguesValue={leaguesValue}
              nationalities={nationalities}
              loadingNationalities={loadingNationalities}
              nationalitiesValue={nationalitiesValue}
              ageValue={ageValue}
              salaryValue={salaryValue}
              monthsRemainingValue={monthsRemainingValue}
              isSmallScreen={isSmallScreen}
              onSelectLeagues={onSelectLeagues}
              onSelectNationalities={onSelectNationalities}
              onChangeAge={onChangeAge}
              onChangeSalary={onChangeSalary}
              onChangeMonthsRemaining={onChangeMonthsRemaining}
            />
          </ScrollView>
        ) : (
          <NestableScrollContainer
            // eslint-disable-next-line no-restricted-syntax -- draggable-flatlist NestableScrollContainer is not an atom surface; runtime 50%-viewport maxHeight, no closed sizing token
            style={ACCORDION_MAX_HEIGHT_STYLE}
            className="flex-1"
            contentContainerClassName={
              !isSmallScreen ? "gap-4 px-6" : "gap-10 px-6 pt-10"
            }
            nestedScrollEnabled
            disableScrollViewPanResponder
            showsVerticalScrollIndicator={false}
          >
            <FilterFieldsView
              groupedLeagues={groupedLeagues}
              loadingLeagues={loadingLeagues}
              leaguesValue={leaguesValue}
              nationalities={nationalities}
              loadingNationalities={loadingNationalities}
              nationalitiesValue={nationalitiesValue}
              ageValue={ageValue}
              salaryValue={salaryValue}
              monthsRemainingValue={monthsRemainingValue}
              isSmallScreen={isSmallScreen}
              onSelectLeagues={onSelectLeagues}
              onSelectNationalities={onSelectNationalities}
              onChangeAge={onChangeAge}
              onChangeSalary={onChangeSalary}
              onChangeMonthsRemaining={onChangeMonthsRemaining}
            />
          </NestableScrollContainer>
        )}
        <Pressable onPress={onReset} className="self-end px-6">
          <Text variant="title-sm">Reset</Text>
        </Pressable>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);

HeaderView.displayName = "HeaderView";

export default memo(HeaderView);
